import React, { useState, useEffect } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import type { ExternalProviderConfig } from '../../types/admin';
import './AdminComponents.css';

const ProviderConfigManagement: React.FC = () => {
  const [configs, setConfigs] = useState<ExternalProviderConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editConfig, setEditConfig] = useState<ExternalProviderConfig | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showSensitive, setShowSensitive] = useState<Record<number, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [filterProvider, setFilterProvider] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Provider tabs state
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  const [providers, setProviders] = useState<string[]>([]);
  const [providerCounts, setProviderCounts] = useState<Record<string, number>>({});
  const [showPassword, setShowPassword] = useState(false);

  const adminApi = useAdminApiService();

  useEffect(() => {
    loadAllProviders();
  }, []);

  useEffect(() => {
    console.log('Configs state updated:', configs);
    console.log('Configs length in state:', configs.length);
    console.log('Pagination state:', {
      currentPage,
      pageSize,
      totalElements,
      totalPages
    });
  }, [configs, currentPage, pageSize, totalElements, totalPages]);

  const loadAllProviders = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading all providers to get provider list...');
      
      const response = await adminApi.getProviderConfigs(0, 1000); // Get all to extract providers
      console.log('All providers response:', response);
      
      const configsData = response.data || [];
      
      // Extract unique providers
      const uniqueProviders: string[] = Array.from(new Set(configsData.map((config: ExternalProviderConfig) => config.provider)));
      setProviders(uniqueProviders);
      
      // Calculate counts for each provider
      const counts: Record<string, number> = {};
      uniqueProviders.forEach(provider => {
        counts[provider] = configsData.filter((config: ExternalProviderConfig) => config.provider === provider).length;
      });
      setProviderCounts(counts);
      
      // Default to first provider instead of showing all
      if (uniqueProviders.length > 0) {
        setSelectedProvider(uniqueProviders[0]);
        loadConfigsByProvider(uniqueProviders[0], 0, pageSize);
      }
    } catch (err: any) {
      console.error('Error loading providers:', err);
      setError(err.response?.data?.message || 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const loadConfigs = async (page: number = currentPage, size: number = pageSize) => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading provider configs...');
      
      const response = await adminApi.getProviderConfigs(page, size);
      console.log('API Response:', response);
      
      // Handle paginated response and filter out null values
      const configsData = (response.data || []).filter((config: any) => config != null);
      console.log('Configs data:', configsData);
      console.log('Configs length:', configsData.length);
      
      setConfigs(configsData);
      
      // Extract unique providers (with null check)
      const uniqueProviders: string[] = Array.from(new Set(configsData.map((config: ExternalProviderConfig) => config.provider).filter((provider: string) => provider != null)));
      setProviders(uniqueProviders);
      
      // Update pagination info from response
      // The pagination metadata should be at the top level of the response
      console.log('Response totalElements:', response.totalElements);
      console.log('Response totalPages:', response.totalPages);
      console.log('Response currentPage:', response.currentPage);
      console.log('Response pageSize:', response.pageSize);
      
      if (response.totalElements !== undefined) {
        setTotalElements(response.totalElements);
        setTotalPages(response.totalPages || 0);
        setCurrentPage(response.currentPage || 0);
        setPageSize(response.pageSize || 10);
      }
    } catch (err: any) {
      console.error('Error loading provider configs:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      setError(err.response?.data?.message || 'Failed to load provider configurations');
    } finally {
      setLoading(false);
    }
  };

  const loadConfigsByProvider = async (provider: string, page: number = 0, size: number = 10) => {
    try {
      setLoading(true);
      setError(null);
      console.log(`Loading configs for provider: ${provider}`);
      
      const response = await adminApi.getProviderConfigsByProvider(provider, page, size);
      console.log('Provider API Response:', response);
      
      const configsData = (response.data || []).filter((config: any) => config != null);
      setConfigs(configsData);
      
      // Update pagination info
      if (response.totalElements !== undefined) {
        setTotalElements(response.totalElements);
        setTotalPages(response.totalPages || 0);
        setCurrentPage(response.currentPage || 0);
        setPageSize(response.pageSize || 10);
      }
    } catch (err: any) {
      console.error('Error loading provider configs:', err);
      setError(err.response?.data?.message || 'Failed to load provider configurations');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    loadConfigsByProvider(selectedProvider, newPage, pageSize);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0); // Reset to first page
    loadConfigsByProvider(selectedProvider, 0, newSize);
  };

  const handleProviderSelect = (provider: string) => {
    setSelectedProvider(provider);
    setCurrentPage(0); // Reset to first page
    loadConfigsByProvider(provider, 0, pageSize);
  };

  const handleEdit = (config: ExternalProviderConfig) => {
    setEditConfig(config);
    setEditValue(config.configValue);
    setShowModal(true);
    setShowPassword(false); // Reset password visibility when opening modal
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSave = async () => {
    if (!editConfig) return;

    try {
      setSaving(true);
      const response = await adminApi.updateProviderConfig(
        editConfig.provider,
        editConfig.configKey,
        editValue
      );
      
      // Update the config in the list
      setConfigs(prevConfigs => 
        prevConfigs.map(cfg => 
          cfg.id === editConfig.id ? response.data : cfg
        )
      );
      
      setShowModal(false);
      setEditConfig(null);
      setEditValue('');
    } catch (err: any) {
      console.error('Error updating config:', err);
      setError(err.response?.data?.message || 'Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditConfig(null);
    setEditValue('');
    setError(null);
  };

  const handleToggleTestnet = async (config: ExternalProviderConfig) => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      
      const newValue = config.configValue === 'true' ? 'false' : 'true';
      const networkType = newValue === 'true' ? 'Sepolia Testnet' : 'Sepolia Mainnet';
      
      const response = await adminApi.updateProviderConfig(
        config.provider,
        config.configKey,
        newValue
      );
      
      // Update the config in the list
      setConfigs(prevConfigs => 
        prevConfigs.map(cfg => 
          cfg.id === config.id ? response.data : cfg
        )
      );
      
      // Show success message
      setSuccessMessage(`${config.provider.toUpperCase()} successfully switched to ${networkType}`);
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
      console.log(`Successfully switched ${config.provider} to ${networkType}`);
    } catch (err: any) {
      console.error('Error toggling testnet:', err);
      setError(err.response?.data?.message || 'Failed to toggle testnet configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleShowSensitive = (id: number) => {
    setShowSensitive(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // No need for filtering since we load data directly for each provider
  const displayConfigs = configs.filter(cfg => cfg != null);

  const uniqueProviders = Array.from(new Set(configs.filter(cfg => cfg != null).map(cfg => cfg.provider)));

  if (loading) {
    return (
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Provider Configurations</h2>
        </div>
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2>External Provider Configurations</h2>
        <div className="admin-section-actions">
          <button 
            onClick={() => loadConfigsByProvider(selectedProvider, currentPage, pageSize)} 
            className="admin-button secondary"
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Provider Tabs */}
      <div className="provider-tabs">
        {providers.map(provider => (
          <button
            key={provider}
            onClick={() => handleProviderSelect(provider)}
            className={`provider-tab ${selectedProvider === provider ? 'active' : ''}`}
          >
            {provider.toUpperCase()} ({providerCounts[provider] || 0})
          </button>
        ))}
      </div>

      {error && (
        <div className="admin-error-message">
          {error}
          <button onClick={() => setError(null)} className="admin-button small">
            Dismiss
          </button>
        </div>
      )}

      {successMessage && (
        <div className="admin-success-message">
          {successMessage}
          <button onClick={() => setSuccessMessage(null)} className="admin-button small">
            Dismiss
          </button>
        </div>
      )}

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Configuration Key</th>
              <th>Value</th>
              <th>Description</th>
              <th>Last Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayConfigs.length === 0 ? (
              <tr>
                <td colSpan={6} className="admin-table-empty">
                  {filterProvider ? 'No configurations found for selected provider' : 'No configurations found'}
                </td>
              </tr>
            ) : (
              displayConfigs.map(config => (
                <tr key={config.id}>
                  <td>
                    <span className="provider-badge">{config.provider}</span>
                  </td>
                  <td>
                    <code className="config-key">{config.configKey}</code>
                  </td>
                  <td>
                    <div className="config-value-container">
                      {config.sensitive && !showSensitive[config.id] ? (
                        <div className="sensitive-value">
                          <span className="masked-value">••••••••••••••••</span>
                          <button 
                            onClick={() => toggleShowSensitive(config.id)}
                            className="admin-button small"
                          >
                            Show
                          </button>
                        </div>
                      ) : (
                        <div className="visible-value">
                          <span className={config.sensitive ? 'sensitive-text' : ''}>
                            {config.configValue}
                          </span>
                          {config.sensitive && (
                            <button 
                              onClick={() => toggleShowSensitive(config.id)}
                              className="admin-button small"
                            >
                              Hide
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="config-description">{config.description}</span>
                  </td>
                  <td>
                    <span className="config-updated">
                      {new Date(config.updatedAt).toLocaleString()}
                    </span>
                  </td>
                  <td>
                    <div className="config-actions">
                      <button 
                        onClick={() => handleEdit(config)}
                        className="admin-button small primary"
                      >
                        Edit
                      </button>
                      
                      {/* Toggle button for blockchain testnet/mainnet */}
                      {(config.configKey === 'use.testnet' || config.configKey.includes('testnet')) && (
                        <button 
                          onClick={() => handleToggleTestnet(config)}
                          className={`admin-button small ${config.configValue === 'true' ? 'danger' : 'success'}`}
                          title={config.configValue === 'true' ? 'Switch to Sepolia Mainnet' : 'Switch to Sepolia Testnet'}
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <span className="loading-spinner"></span>
                              Switching...
                            </>
                          ) : (
                            <>
                              {config.configValue === 'true' ? '🌐 Sepolia Mainnet' : '🧪 Sepolia Testnet'}
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalElements > 0 && (
        <div className="admin-pagination">
          <div className="pagination-info">
            <span>
              Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} configurations
            </span>
          </div>
          
          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0}
              className="admin-button small"
            >
              Previous
            </button>
            
            <div className="page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(0, Math.min(totalPages - 5, currentPage - 2)) + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`admin-button small ${currentPage === pageNum ? 'primary' : 'secondary'}`}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1}
              className="admin-button small"
            >
              Next
            </button>
          </div>
          
          <div className="page-size-control">
            <label>Show:</label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="admin-select small"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span>per page</span>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showModal && editConfig && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>Edit Configuration</h3>
              <button onClick={handleCancel} className="admin-modal-close">&times;</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label>Provider:</label>
                <span className="admin-form-value">{editConfig.provider}</span>
              </div>
              <div className="admin-form-group">
                <label>Configuration Key:</label>
                <span className="admin-form-value">{editConfig.configKey}</span>
              </div>
              <div className="admin-form-group">
                <label>Description:</label>
                <span className="admin-form-value">{editConfig.description}</span>
              </div>
              <div className="admin-form-group">
                <label htmlFor="configValue">
                  Value {editConfig.sensitive && '(Sensitive)'}:
                </label>
                <div className="password-input-container">
                  <input
                    id="configValue"
                    type={editConfig.sensitive && !showPassword ? 'password' : 'text'}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="admin-input"
                    placeholder="Enter configuration value"
                  />
                  {editConfig.sensitive && (
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="password-toggle-btn"
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button 
                onClick={handleCancel} 
                className="admin-button secondary"
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                onClick={handleSave} 
                className="admin-button primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderConfigManagement; 